import React, { useRef, useState } from "react";
import { View, StyleSheet, Pressable, GestureResponderEvent, Modal, ActivityIndicator } from "react-native";
import CustomText from "../common/general/CustomText/CustomText";
import AssessmentAudioVisualizer from "../../containers/assessment/sharedFiles/AssessmentAudioVisualizer";
import { MicrophoneIcon, CheckCircleIcon, CaretDownIcon } from "@phosphor-icons/react";

interface MicrophoneDevice {
  deviceId: string;
  label: string;
  groupId?: string;
}

interface MicrophoneSelectorDropdownProps {
  devices: MicrophoneDevice[];
  selectedDeviceId: string;
  onDeviceSelect: (deviceId: string) => void;
  disabled?: boolean;
  isSettingDefault?: boolean;
  isAudioActive?: boolean;
  isCompact?: boolean;
  isLoading?: boolean;
  error?: string | null;
}

const MicrophoneSelectorDropdown: React.FC<MicrophoneSelectorDropdownProps> = ({
  devices,
  selectedDeviceId,
  onDeviceSelect,
  disabled = false,
  isSettingDefault = false,
  isAudioActive = false,
  isCompact = false,
  isLoading = false,
  error = null,
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const buttonRef = useRef<View>(null);

  const handleOpenMenu = (event: GestureResponderEvent) => {
    if (disabled || isSettingDefault) return;
    setMenuVisible(true);
  };

  const handleCloseMenu = () => {
    setMenuVisible(false);
  };

  const handleDeviceSelect = (deviceId: string) => {
    setMenuVisible(false);
    onDeviceSelect(deviceId);
  };

  // Compact loading state
  if (isCompact && isLoading) {
    return (
      <View style={styles.compactContainer}>
        <View style={[styles.dropdown, styles.dropdownCompact, disabled && styles.dropdownDisabled]}>
          <ActivityIndicator
            size="small"
            color="#344054"
            accessibilityLabel="Loading microphones"
            aria-label="Loading microphones"
          />
        </View>
      </View>
    );
  }

  // Compact error state
  if (isCompact && error) {
    return (
      <View style={styles.compactContainer}>
        <View style={[styles.dropdown, styles.dropdownCompact, styles.dropdownError]}>
          <MicrophoneIcon fill="#F04438" weight="bold" size={16} />
        </View>
      </View>
    );
  }

  // Compact no devices state
  if (isCompact && devices.length === 0 && !isLoading) {
    return (
      <View style={styles.compactContainer}>
        <View style={[styles.dropdown, styles.dropdownCompact, disabled && styles.dropdownDisabled]}>
          <MicrophoneIcon fill="#667085" weight="bold" size={16} />
        </View>
      </View>
    );
  }

  return (
    <View style={isCompact ? styles.compactContainer : styles.dropdownContainer}>
      <Pressable
        ref={buttonRef}
        style={[styles.dropdown, isCompact && styles.dropdownCompact, disabled && styles.dropdownDisabled]}
        onPress={handleOpenMenu}
        disabled={disabled || isSettingDefault}
        role="button"
        aria-label="Select microphone"
        aria-haspopup="listbox"
        aria-expanded={menuVisible}
        accessibilityRole="button"
        accessibilityLabel="Select microphone"
        accessibilityHint="Opens microphone selection menu"
        accessibilityState={{ disabled: disabled || isSettingDefault }}>
        {isSettingDefault ? (
          <View style={styles.dropdownButton}>
            <ActivityIndicator
              size="small"
              color="#344054"
              accessibilityLabel="Updating microphone"
              aria-label="Updating microphone"
            />
          </View>
        ) : (
          <View style={styles.dropdownButtonContainer}>
            <MicrophoneIcon fill="#000" weight="bold" size={isCompact ? 16 : undefined} />
            <AssessmentAudioVisualizer deviceId={selectedDeviceId} isActive={isAudioActive} />
            <CaretDownIcon fill="#000" weight="bold" size={16} />
          </View>
        )}
      </Pressable>

      <Modal
        visible={menuVisible}
        transparent={true}
        animationType="none"
        onRequestClose={handleCloseMenu}
        statusBarTranslucent={false}>
        <Pressable
          style={styles.modalOverlay}
          onPress={handleCloseMenu}
          role="button"
          aria-label="Close microphone menu"
          accessibilityRole="button"
          accessibilityLabel="Close microphone menu">
          <View style={styles.customMenu}>
            {devices.map((device) => {
              const isSelected = device.deviceId === selectedDeviceId;
              return (
                <Pressable
                  key={device.deviceId}
                  style={styles.menuItem}
                  onPress={() => handleDeviceSelect(device.deviceId)}
                  role="option"
                  aria-selected={isSelected}
                  accessibilityRole="menuitem"
                  accessibilityLabel={device.label}
                  accessibilityState={{ selected: isSelected }}>
                  <CheckCircleIcon size={24} weight="fill" color={isSelected ? "#4BA30D" : "#D0D5DD"} />
                  <CustomText
                    text={device.label}
                    size="s"
                    style={styles.menuItemText}
                    weight="normal"
                    useTranslationText={false}
                  />
                </Pressable>
              );
            })}
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  compactContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  dropdownContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  dropdown: {
    backgroundColor: "#F2F4F7",
    borderRadius: 360,
    borderWidth: 0,
    height: 32,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  dropdownCompact: {
    minWidth: 32,
    paddingHorizontal: 6,
  },
  dropdownDisabled: {
    backgroundColor: "#F2F4F7",
    opacity: 0.6,
  },
  dropdownError: {
    backgroundColor: "#FEF3F2",
    borderWidth: 1,
    borderColor: "#F04438",
  },
  dropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  dropdownButtonContainer: {
    paddingHorizontal: 0,
    paddingLeft: 4,
    paddingRight: 3,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "transparent",
    justifyContent: "center",
    alignItems: "center",
  },
  customMenu: {
    backgroundColor: "#fff",
    borderRadius: 20,
    paddingHorizontal: 20,
    paddingBottom: 10,
    paddingTop: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
    minWidth: 200,
    maxWidth: 400,
    marginTop: 250,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginBottom: 10,
  },
  menuItemText: {
    color: "#344054",
  },
});

export default MicrophoneSelectorDropdown;
